import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { AccountSettingsService } from 'src/app/shared-layout/header/account-settings/account-setting.service';
import { UserManagementService } from 'src/app/user-management/user-management.service';
import { StateManagementService } from 'src/app/shared/services/state-management.service';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { userTypeResponse } from 'src/app/shared/constants/response-mapping';
import { MFValidators } from 'src/app/shared/validators/mf-validators';
import {
  DuplicateProfile,
  isGeneratedProfileEmail,
  MergedAccessEvent,
  normalizeProfilePhone,
  PopupProfile,
  profileEmail,
  ProfileManagementService,
  ProfileUpdatedEvent,
  profilePopupErrorKey,
  SiteUserUpdate,
} from '../profile-management.service';
import { isSitewideUserType } from '../profile-assign-user-type/profile-assign-user-type.component';

function formatPhoneForEdit(value?: string): string {
  const raw = String(value || '').trim();
  const digits = raw.replace(/\D/g, '');
  const nationalDigits =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (nationalDigits.length === 10) {
    return `(${nationalDigits.slice(0, 3)}) ${nationalDigits.slice(
      3,
      6
    )}-${nationalDigits.slice(6)}`;
  }
  return raw;
}

@Component({
  selector: 'app-profile-edit',
  templateUrl: './profile-edit.component.html',
  styleUrls: ['./profile-edit.component.scss'],
})
export class ProfileEditComponent implements OnInit, OnDestroy {
  @Input() profileDetails!: PopupProfile;
  @Input() siteId!: string;
  @Input() dialogRef?: { close: () => void };
  @Input() stepper = 17;
  @Input() fullEdit: boolean | undefined = false;
  @Input() editField: 'notes' | 'profile' | 'phone' | undefined = 'profile';
  @Input() mobileSetupMode = false;
  @Input() confirmAccessEdit = false;
  // Complete User Info: email and phone required for contact-required user types.
  @Input() completeInfo = false;
  @Input() backStep = 1;
  @Input() nextStep = 1;
  @Output() stepperChange = new EventEmitter<number>();
  @Output() profileUpdated = new EventEmitter<ProfileUpdatedEvent>();
  @Output() openProfile = new EventEmitter<string>();
  @Output() mergedNeedsAccess = new EventEmitter<MergedAccessEvent>();

  form!: FormGroup;
  isSubmitting = false;
  isRefreshingProfile = false;
  errorKey = '';
  duplicateUsers: DuplicateProfile[] = [];
  duplicateKind: 'name' | 'contact' | null = null;
  private pendingUpdate?: SiteUserUpdate;
  private pendingProfileRefresh?: {
    previousEmail: string;
    updatedEmail: string;
    merged: boolean;
    routeMergedAccess: boolean;
  };
  private destroyed = new Subject<void>();

  get editingProfile(): boolean {
    return this.fullEdit === true && this.editField === 'profile';
  }

  get editingPhone(): boolean {
    return (
      !this.completeInfo && !this.mobileSetupMode && this.editField === 'phone'
    );
  }

  get editingNotes(): boolean {
    return (
      !this.completeInfo && !this.mobileSetupMode && this.editField === 'notes'
    );
  }

  get profileForm(): boolean {
    return this.completeInfo || this.mobileSetupMode || this.editingProfile;
  }

  get existingUser(): DuplicateProfile | undefined {
    return this.duplicateKind === 'contact'
      ? this.duplicateUsers[0]
      : undefined;
  }

  get existingUserMaskedName(): string {
    const name = this.duplicateUserName(this.existingUser || {});
    if (!name) return '—';
    if (this.existingUser?.isAssociatedWithSite === true) return name;
    const firstName = name.split(/\s+/)[0];
    return firstName + '*'.repeat(Math.max(name.length - firstName.length, 1));
  }

  get existingUserMaskedPhone(): string {
    if (this.existingUser?.isAssociatedWithSite === true) {
      return (
        formatPhoneForEdit(this.existingUser.phoneNumber || undefined) || '—'
      );
    }
    const digits = this.existingUser?.phoneNumber?.replace(/\D/g, '');
    return digits ? `***-***-**${digits.slice(-2)}` : '—';
  }

  get existingUserType(): string {
    return userTypeResponse[this.existingUser?.userType || 9] || 'Unassigned';
  }

  get existingUserEmail(): string {
    return profileEmail(this.existingUser || undefined);
  }

  get canMergeExistingAccount(): boolean {
    const source = this.profileDetails;
    const temporarySource =
      String(source?.isTemporaryUser).toLowerCase() === 'true' ||
      isGeneratedProfileEmail(source?.email);
    const target = this.existingUser;
    return (
      this.profileForm &&
      temporarySource &&
      !!target?.email &&
      typeof target.isAssociatedWithSite === 'boolean' &&
      String(target.isTemporaryUser).toLowerCase() !== 'true' &&
      !isGeneratedProfileEmail(target.email) &&
      this.pendingUpdate?.newEmail?.toLowerCase() ===
        target.email.toLowerCase() &&
      !!source.email &&
      source.email.toLowerCase() !== target.email.toLowerCase()
    );
  }

  // Contact duplicates are email-only; a phone conflict comes back as an inline error.
  get showExistingAccount(): boolean {
    return (
      this.duplicateKind === 'contact' &&
      this.existingUser?.isAssociatedWithSite !== true
    );
  }

  get showUserAlreadyOnSite(): boolean {
    return (
      this.duplicateKind === 'contact' &&
      this.existingUser?.isAssociatedWithSite === true
    );
  }

  get awaitingProfileRefresh(): boolean {
    return !!this.pendingProfileRefresh;
  }

  constructor(
    private fb: FormBuilder,
    private service: ProfileManagementService,
    private accountSettingsService: AccountSettingsService,
    private userManagementService: UserManagementService,
    private stateManagementService: StateManagementService
  ) {}

  ngOnInit(): void {
    const namePattern =
      /^[A-Za-zÀ-ÖØ-öø-ÿ](?:[A-Za-zÀ-ÖØ-öø-ÿ0-9]|[',.%&()/# -]+(?=[A-Za-zÀ-ÖØ-öø-ÿ0-9]))*$/;
    const names = [
      Validators.required,
      Validators.maxLength(32),
      Validators.pattern(namePattern),
    ];
    const email = (control: AbstractControl) =>
      control.value ? MFValidators.email(control) : null;
    const realEmail = (control: AbstractControl) =>
      isGeneratedProfileEmail(control.value) ? { email: true } : null;
    this.form = this.fb.group({
      firstName: [this.profileDetails?.firstName || '', names],
      middleName: [
        this.profileDetails?.middleName || '',
        [Validators.maxLength(32), Validators.pattern(namePattern)],
      ],
      lastName: [this.profileDetails?.lastName || '', names],
      email: [
        profileEmail(this.profileDetails),
        this.completeInfo || this.mobileSetupMode
          ? [Validators.required, email, realEmail]
          : [email],
      ],
      phoneNumber: [
        this.editingProfile || this.editingPhone
          ? formatPhoneForEdit(this.profileDetails?.phoneNumber)
          : this.profileDetails?.phoneNumber || '',
        this.completeInfo || this.mobileSetupMode || this.editingPhone
          ? [Validators.required, MFValidators.usCaPhone]
          : MFValidators.usCaPhone,
      ],
      notes: [
        this.profileDetails?.notes || '',
        this.editingProfile && !this.completeInfo && !this.mobileSetupMode
          ? [Validators.required, Validators.maxLength(100)]
          : Validators.maxLength(100),
      ],
    });
    if (!this.profileForm) {
      ['firstName', 'middleName', 'lastName', 'email'].forEach((key) =>
        this.form.get(key)?.disable()
      );
      if (!this.editingPhone) this.form.get('phoneNumber')?.disable();
      if (this.editingPhone) this.form.get('notes')?.disable();
    }
    if (this.completeInfo) {
      // Required contacts must show their validation state as soon as this step opens.
      ['email', 'phoneNumber'].forEach((key) =>
        this.form.get(key)?.markAsTouched()
      );
    }
  }

  save(): void {
    if (
      this.isSubmitting ||
      this.isRefreshingProfile ||
      this.pendingProfileRefresh ||
      this.duplicateKind
    )
      return;
    this.errorKey = '';
    ['firstName', 'middleName', 'lastName', 'email'].forEach((key) => {
      const control = this.form.get(key)!;
      control.setValue(String(control.value || '').trim());
    });
    this.form.markAllAsTouched();
    if (this.form.invalid) return;
    const email = this.profileDetails?.email;
    if (!this.siteId || !email) {
      this.errorKey = 'PROFILE_POPUP_SAVE_FAILED';
      return;
    }
    const notes = String(this.form.get('notes')!.value || '');
    if (this.completeInfo || this.mobileSetupMode) {
      const value = this.form.getRawValue();
      // Always the current identity: the backend migrates generated emails via newEmail.
      this.updateProfile({
        siteId: this.siteId,
        email,
        notes,
        firstName: value.firstName,
        middleName: value.middleName,
        lastName: value.lastName,
        newEmail: value.email.toLowerCase(),
        phoneNumber: normalizeProfilePhone(value.phoneNumber),
      });
    } else if (this.editingPhone) {
      const phoneNumber = normalizeProfilePhone(
        String(this.form.get('phoneNumber')!.value || '')
      );
      if (this.isPendingActivation) {
        const user = this.stateManagementService.getUser();
        if (
          user?.userType !== CONSTANTS.PROPERTY_MANAGER_ADMIN &&
          user?.userType !== CONSTANTS.PROPERTY_MANAGER_USER
        ) {
          this.errorKey = 'YOU_ARE_NOT_AUTHORIZED_TO_MAKE_THIS_CALL';
          return;
        }
        this.isSubmitting = true;
        this.userManagementService
          .updatePendingUserPhoneNumber(this.siteId, email, phoneNumber)
          .pipe(
            takeUntil(this.destroyed),
            finalize(() => (this.isSubmitting = false))
          )
          .subscribe({
            next: (success) => {
              if (success) this.complete(email, email, { phoneNumber });
              else this.errorKey = 'FAILED_TO_UPDATE_PHONE_NUMBER_TRY_AGAIN';
            },
            error: (error) =>
              (this.errorKey = profilePopupErrorKey(
                error,
                'FAILED_TO_UPDATE_PHONE_NUMBER_TRY_AGAIN'
              )),
          });
        return;
      }
      this.updateProfile({
        siteId: this.siteId,
        email,
        phoneNumber,
        notes: this.profileDetails.notes || '',
      });
    } else if (this.fullEdit === true) {
      const value = this.form.getRawValue();
      const update: SiteUserUpdate = { siteId: this.siteId, email, notes };
      if (this.editingProfile) {
        Object.assign(update, {
          firstName: value.firstName,
          middleName: value.middleName,
          lastName: value.lastName,
          phoneNumber: normalizeProfilePhone(value.phoneNumber),
        });
        // An empty optional email means retain the backend identity, never erase it.
        if (value.email) update.newEmail = value.email.toLowerCase();
      }
      this.updateProfile(update);
    } else if (
      this.profileDetails?.invitationPending === true ||
      String(this.profileDetails?.invitationPending) === 'true'
    ) {
      this.isSubmitting = true;
      this.service
        .updatePendingUserNotes(this.siteId, email, notes)
        .pipe(
          takeUntil(this.destroyed),
          finalize(() => (this.isSubmitting = false))
        )
        .subscribe({
          next: () => this.complete(email, email, { notes }),
          error: (error) =>
            (this.errorKey = profilePopupErrorKey(
              error,
              'PROFILE_POPUP_SAVE_FAILED'
            )),
        });
    } else {
      this.updateProfile({ siteId: this.siteId, email, notes });
    }
  }

  confirmDuplicate(): void {
    if (
      this.isSubmitting ||
      this.duplicateKind !== 'name' ||
      !this.pendingUpdate
    )
      return;
    const update = { ...this.pendingUpdate, isDuplicateNameVerified: true };
    this.dismissDuplicate();
    this.updateProfile(update);
  }

  dismissDuplicate(): void {
    if (
      this.isSubmitting ||
      this.isRefreshingProfile ||
      this.pendingProfileRefresh
    )
      return;
    this.errorKey = '';
    this.duplicateKind = null;
    this.duplicateUsers = [];
    this.pendingUpdate = undefined;
  }

  continueWithExistingUser(): void {
    if (
      this.isSubmitting ||
      this.isRefreshingProfile ||
      this.pendingProfileRefresh ||
      !this.existingUser
    )
      return;
    if (!this.canMergeExistingAccount || !this.pendingUpdate) {
      this.openDuplicateProfile(this.existingUser);
      return;
    }
    this.errorKey = '';
    this.updateProfile({ ...this.pendingUpdate, mergeWithExistingEmail: true });
  }

  retryProfileRefresh(): void {
    if (this.isRefreshingProfile || !this.pendingProfileRefresh) return;
    this.refreshUpdatedProfile(
      this.pendingProfileRefresh.previousEmail,
      this.pendingProfileRefresh.updatedEmail,
      this.pendingProfileRefresh.merged,
      this.pendingProfileRefresh.routeMergedAccess
    );
  }

  back(): void {
    if (this.duplicateKind) {
      this.dismissDuplicate();
      return;
    }
    if (!this.isSubmitting && !this.isRefreshingProfile) {
      this.stepperChange.emit(
        this.completeInfo ? this.backStep : this.mobileSetupMode ? 18 : 1
      );
    }
  }

  // Leaves the assign flow; nothing granted so far is transferred to the other user.
  openDuplicateProfile(user?: DuplicateProfile): void {
    if (this.isSubmitting || !user?.email) return;
    this.openProfile.emit(user.email);
  }

  duplicateUserName(user: Partial<DuplicateProfile>): string {
    return [user.firstName, user.middleName, user.lastName]
      .filter(Boolean)
      .join(' ');
  }

  duplicateUserInitials(user: DuplicateProfile): string {
    return (
      [user.firstName, user.lastName]
        .filter(Boolean)
        .map((part) => part![0])
        .join('')
        .slice(0, 2)
        .toUpperCase() || '—'
    );
  }

  duplicateUserType(user: DuplicateProfile): string {
    const roleLabels: { [type: number]: string } = {
      1: 'PMA',
      2: 'PMU',
      3: 'IA',
      4: 'IU',
      5: 'RA',
      6: 'R',
      7: 'R',
      8: 'EOL Tester',
      9: 'Unassigned',
    };
    return user.userType != null
      ? roleLabels[user.userType] || String(user.userType)
      : '—';
  }

  close(): void {
    if (!this.isSubmitting && !this.isRefreshingProfile)
      this.dialogRef?.close();
  }

  private get isPendingActivation(): boolean {
    const flagIsTrue = (value: unknown) =>
      value === true || String(value).toLowerCase() === 'true';
    return (
      flagIsTrue(this.profileDetails?.invitationPending) &&
      !flagIsTrue(this.profileDetails?.isDeactivated) &&
      !flagIsTrue(this.profileDetails?.isTemporaryUser)
    );
  }

  private updateProfile(update: SiteUserUpdate): void {
    update = {
      ...update,
      mergeWithExistingEmail: update.mergeWithExistingEmail === true,
    };
    this.isSubmitting = true;
    this.service
      .updateSiteUser(update)
      .pipe(
        takeUntil(this.destroyed),
        finalize(() => (this.isSubmitting = false))
      )
      .subscribe({
        next: (result) => {
          if (result.duplicateEmailUsers || result.duplicateNameUsers.length) {
            // A new conflict must require a fresh, explicit merge confirmation.
            this.pendingUpdate = { ...update, mergeWithExistingEmail: false };
            this.duplicateKind = result.duplicateEmailUsers
              ? 'contact'
              : 'name';
            this.duplicateUsers = result.duplicateEmailUsers
              ? [result.duplicateEmailUsers]
              : result.duplicateNameUsers;
            return;
          }
          if (update.mergeWithExistingEmail) {
            this.refreshUpdatedProfile(
              update.email,
              result.email,
              true,
              this.confirmAccessEdit
            );
            return;
          }
          const fields: Partial<PopupProfile> = this.editingPhone
            ? { phoneNumber: update.phoneNumber }
            : { notes: update.notes };
          fields.stagedEmail = result.stagedEmail ?? null;
          if (this.profileForm) {
            Object.assign(fields, {
              firstName: update.firstName,
              middleName: update.middleName,
              lastName: update.lastName,
              phoneNumber: update.phoneNumber,
            });
          }
          if (this.completeInfo) {
            this.complete(update.email, result.email, fields);
          } else if (this.mobileSetupMode) {
            this.refreshUpdatedProfile(update.email, result.email);
          } else {
            this.complete(update.email, result.email, fields);
          }
        },
        error: (error) =>
          (this.errorKey = profilePopupErrorKey(
            error,
            'PROFILE_POPUP_SAVE_FAILED'
          )),
      });
  }

  private refreshUpdatedProfile(
    previousEmail: string,
    updatedEmail: string,
    merged = false,
    routeMergedAccess = false
  ): void {
    this.pendingProfileRefresh = {
      previousEmail,
      updatedEmail,
      merged,
      routeMergedAccess,
    };
    this.isRefreshingProfile = true;
    this.errorKey = '';
    this.accountSettingsService
      .getSpecficUserProfileInfo(this.siteId, updatedEmail)
      .pipe(
        takeUntil(this.destroyed),
        finalize(() => (this.isRefreshingProfile = false))
      )
      .subscribe({
        next: (latestProfile: PopupProfile) => {
          const profileUserType = Number(latestProfile.userTypeId);
          const duplicateUserType = Number(this.existingUser?.userType);
          const mergedUserType =
            Number.isFinite(profileUserType) && profileUserType > 0
              ? profileUserType
              : duplicateUserType;
          if (merged)
            this.profileDetails = {
              ...latestProfile,
              email: updatedEmail,
              stagedEmail: latestProfile.stagedEmail ?? null,
            };
          else
            Object.assign(this.profileDetails, latestProfile, {
              email: updatedEmail,
              stagedEmail: latestProfile.stagedEmail ?? null,
            });
          this.pendingProfileRefresh = undefined;
          this.duplicateKind = null;
          this.duplicateUsers = [];
          this.pendingUpdate = undefined;
          this.profileUpdated.emit({
            previousEmail,
            email: updatedEmail,
            profileDetails: this.profileDetails,
            merged,
          });
          if (
            merged &&
            routeMergedAccess &&
            Number.isFinite(mergedUserType) &&
            mergedUserType > 0
          ) {
            this.mergedNeedsAccess.emit({
              email: updatedEmail,
              userType: mergedUserType,
              mode: isSitewideUserType(mergedUserType) ? 'confirm' : 'assign',
            });
          } else if (merged) this.openProfile.emit(updatedEmail);
          else this.stepperChange.emit(31);
        },
        error: () => (this.errorKey = 'REQUEST_TO_GET_USER_INFO_FAILED'),
      });
  }

  private complete(
    previousEmail: string,
    email: string,
    fields: Partial<PopupProfile>
  ): void {
    Object.assign(this.profileDetails, fields, { email });
    // Backend migration uses user:<email>; retain any existing prefix convention.
    if (previousEmail.toLowerCase() !== email.toLowerCase()) {
      const id = this.profileDetails.userId;
      if (id === previousEmail) this.profileDetails.userId = email;
      else if (id?.endsWith(`:${previousEmail}`)) {
        this.profileDetails.userId = id.slice(0, -previousEmail.length) + email;
      }
      this.profileDetails.invitationPending = false;
    }
    if (this.profileForm) {
      this.profileDetails.name = [
        this.profileDetails.firstName,
        this.profileDetails.middleName,
        this.profileDetails.lastName,
      ]
        .filter(Boolean)
        .join(' ');
    }
    this.profileUpdated.emit({
      previousEmail,
      email,
      profileDetails: this.profileDetails,
    });
    this.stepperChange.emit(this.nextStep);
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }
}
